import type { PostEntityType } from "./tanstack-query/dtos/post.dto";
import type { ContributionVisibility } from "./tanstack-query/contributions";

export type DashboardTabs =
  | "for-you"
  | "projects"
  | "portfolios"
  | "challenges"
  | "contributions"
  | "access"
  | "invitations";

export type PublicProfileTabs =
  | "projects"
  | "publications"
  | "portfolios"
  | "contributions"
  | "funding-pools"
  | "following";

export type ProjectTabs =
  | "overview"
  | "plan"
  | "documents"
  | "submissions"
  | "contributions"
  | "comments"
  | "posts";

export type ProjectEditTabs =
  | "dashboard"
  | "basics"
  | "research"
  | "funding"
  | "submissions"
  | "documents"
  | "people"
  | "posts"
  | "activity"
  | "revisions"
  | "settings";

interface FundingParams {
  openFunding?: boolean;
  amount?: string;
  isPledge?: boolean;
  visibility?: ContributionVisibility;
}

interface RequestAccessParams {
  requestAccess?: boolean;
  message?: string;
}

interface ProjectsParams {
  category?: string | string[];
  portfolio?: string;
}

export const toHome = () => "/";
export const toPeople = (params?: { portfolio?: string }) =>
  params?.portfolio ? `/people?portfolio=${params.portfolio}` : "/people";
export const toAbout = () => "/about";
export const toFAQ = () => `/faq`;
export const toPrivacy = () => `/privacy`;
export const toTerms = () => `/terms`;
export const toCareers = () => "/careers";
export const toCareer = (slug: string) => `/careers/${slug}`;
export const toNsfPilot = () => "/nsf-pilot";

/** An outreach resume link. The token is the credential, so it is never logged. */
export const toNsfPilotResume = (token: string) =>
  `/nsf-pilot/r/${encodeURIComponent(token)}`;

/** Immersive full-page flows that render without the app chrome. */
const CHROMELESS_PATHS = ["/onboarding", "/nsf-pilot"];

/**
 * Matches on a path segment rather than a plain prefix, so `/nsf-pilot/r/:token`
 * stays chromeless while a future `/nsf-pilot-archive` would not be swept in.
 */
export const isChromelessPath = (pathname: string) =>
  CHROMELESS_PATHS.some(
    (base) => pathname === base || pathname.startsWith(`${base}/`),
  );

export const DISCOVER_PATHS = [
  "/discover",
  "/people",
  "/projects",
  "/portfolios",
  "/funding-pools",
] as const;

export const isDiscoverPath = (pathname: string) => {
  const normalizedPath =
    pathname.endsWith("/") && pathname !== "/"
      ? pathname.slice(0, -1)
      : pathname;
  return DISCOVER_PATHS.includes(
    normalizedPath as (typeof DISCOVER_PATHS)[number],
  );
};

export const withDiscoverQuery = (
  path: string,
  searchParams: URLSearchParams,
) => {
  const q = searchParams.get("q")?.trim();
  if (!q) return path;
  return `${path}?${new URLSearchParams({ q }).toString()}`;
};

export type DiscoverEntityTab =
  | "all"
  | "projects"
  | "people"
  | "portfolios"
  | "funding-pools";

/** Unified discovery. `q` is the source of truth; `theme` is selection state. */
export const toDiscover = (params?: {
  q?: string;
  theme?: string;
  type?: DiscoverEntityTab;
  sort?: string;
  view?: "grid" | "row";
}) => {
  const search = new URLSearchParams();
  if (params?.q?.trim()) search.set("q", params.q.trim());
  if (params?.theme) search.set("theme", params.theme);
  if (params?.type && params.type !== "all") search.set("type", params.type);
  if (params?.sort && params.sort !== "relevance")
    search.set("sort", params.sort);
  if (params?.view && params.view !== "grid") search.set("view", params.view);
  const qs = search.toString();
  return `/discover${qs ? `?${qs}` : ""}`;
};

/** Public, resumable personalization flow (signed-out entry point). */
export const toPersonalize = () => "/personalize";

/** Public, revocable snapshot of someone's personalization result. */
export const toPersonalizationShare = (slug: string) => `/p/${slug}`;

export const toLogin = (redirect?: string) =>
  `/login${redirect ? `?redirect=${encodeURIComponent(redirect)}` : ""}`;
export const toSignup = (
  redirect?: string,
  prefill?: { name?: string; email?: string; interests?: string[] },
) => {
  const params = new URLSearchParams();
  if (redirect) params.set("redirect", redirect);
  if (prefill?.name?.trim()) params.set("name", prefill.name.trim());
  if (prefill?.email?.trim()) params.set("email", prefill.email.trim());
  if (prefill?.interests && prefill.interests.length > 0) {
    params.set("interests", prefill.interests.join(","));
  }
  const qs = params.toString();
  return `/signup${qs ? `?${qs}` : ""}`;
};
export const toForgotPassword = (redirect?: string) =>
  `/forgot-password${redirect ? `?redirect=${encodeURIComponent(redirect)}` : ""}`;
/**
 * `create` is for an account that has no password yet — one made by opening an
 * emailed link — so the page asks them to choose one rather than telling them
 * to reset something they never set.
 */
export const toResetPassword = (
  redirect?: string,
  options?: { create?: boolean },
) => {
  const params = new URLSearchParams();
  if (redirect) params.set("redirect", redirect);
  if (options?.create) params.set("create", "1");
  const query = params.toString();
  return `/reset-password${query ? `?${query}` : ""}`;
};

/** OTP / email-link intermediate page (SafeLinks-safe; does not verify on load). */
export type AuthContinueType = "recovery" | "magiclink" | "email";

export const toAuthContinue = (opts?: {
  type?: AuthContinueType;
  redirect?: string;
  email?: string;
  token?: string;
  tokenHash?: string;
}) => {
  const params = new URLSearchParams();
  if (opts?.type) params.set("type", opts.type);
  if (opts?.redirect) params.set("redirect", opts.redirect);
  if (opts?.email) params.set("email", opts.email);
  if (opts?.token) params.set("token", opts.token);
  if (opts?.tokenHash) params.set("token_hash", opts.tokenHash);
  const qs = params.toString();
  return `/auth/continue${qs ? `?${qs}` : ""}`;
};

export const toSettings = () => `/settings`;
export const toInbox = (conversationId?: string) =>
  conversationId ? `/inbox/${conversationId}` : "/inbox";
export const toDirectMessageDraft = (userId: string) =>
  `/inbox?to=${encodeURIComponent(userId)}`;
export const toProfile = () => `/profile`;
export const toPublicProfile = (idOrHandle: string, params?: FundingParams) =>
  `/profile/${idOrHandle}${params ? `?${new URLSearchParams(Object.entries(params)).toString()}` : ""}`;
export const toOnboarding = (redirect?: string, params?: { from?: string }) => {
  const search = new URLSearchParams();
  if (redirect) search.set("redirect", redirect);
  if (params?.from) search.set("from", params.from);
  const qs = search.toString();
  return `/onboarding${qs ? `?${qs}` : ""}`;
};
export const toDashboard = (
  tab?: DashboardTabs,
  params?: Record<string, string>,
) =>
  `/dashboard${tab ? `?tab=${tab}` : ""}${tab && params ? "&" : params ? "?" : ""}${params ? `${new URLSearchParams(Object.entries(params)).toString()}` : ""}`;
export const toTeamDashboard = (id: string) => `/teams/${id}/dashboard`;

/**
 * Where to go after deleting a project, portfolio, or funding pool.
 * Returns null when already on a dashboard so the current tab is kept
 * (navigating to `/dashboard` would land on For You).
 */
export const afterEntityDeletePath = (
  pathname: string,
  fallback: string,
  teamId?: string | null,
): string | null => {
  if (pathname === "/dashboard") return null;
  if (teamId && pathname === toTeamDashboard(teamId)) return null;
  return fallback;
};

export const toPortfolios = () => "/portfolios";
export const toPortfolio = (
  idOrSlug: string,
  params?: FundingParams,
  section?: string,
) =>
  `/portfolios/${idOrSlug}${params ? `?${new URLSearchParams(Object.entries(params)).toString()}` : ""}${section ? `#${section}` : ""}`;
export const toPortfolioCurate = (idOrSlug: string) =>
  `/portfolios/${idOrSlug}/curate`;

export const toProjects = (params?: ProjectsParams) =>
  `/projects${params ? `?${new URLSearchParams(Object.entries(params)).toString()}` : ""}`;
export const toProject = (
  idOrSlug: string,
  params?: FundingParams & { tab?: ProjectTabs } & RequestAccessParams,
  section?: string,
) =>
  `/projects/${idOrSlug}${params ? `?${new URLSearchParams(Object.entries(params)).toString()}` : ""}${section ? `#${section}` : ""}`;
export const toProjectEdit = (
  idOrSlug: string,
  tab?: ProjectEditTabs,
  section?: string,
) =>
  `/projects/${idOrSlug}/edit${tab ? `?tab=${tab}` : ""}${section ? `#${section}` : ""}`;

export const toFundingPool = (
  idOrSlug: string,
  params?: FundingParams,
  section?: string,
) =>
  `/funding-pools/${idOrSlug}${params ? `?${new URLSearchParams(Object.entries(params)).toString()}` : ""}${section ? `#${section}` : ""}`;
export const toFundingPools = (params?: { portfolio?: string }) =>
  params?.portfolio
    ? `/funding-pools?portfolio=${params.portfolio}`
    : "/funding-pools";

export type FundingPoolEditTabs =
  | "dashboard"
  | "basics"
  | "people"
  | "funding"
  | "details"
  | "evaluation"
  | "submissions"
  | "disbursements"
  | "documents"
  | "posts"
  | "activity"
  | "revisions"
  | "settings";

export const toFundingPoolEdit = (
  idOrSlug: string,
  tab?: FundingPoolEditTabs,
  section?: string,
) =>
  `/funding-pools/${idOrSlug}/edit${tab ? `?tab=${tab}` : ""}${section ? `#${section}` : ""}`;

export type PortfolioEditTabs =
  | "dashboard"
  | "basics"
  | "people"
  | "projects"
  | "posts"
  | "activity"
  | "revisions"
  | "settings";

export type PortfolioTabs = "overview" | "comments" | "posts";

export type FundingPoolTabs =
  | "overview"
  | "submissions"
  | "contributions"
  | "disbursements"
  | "documents"
  | "comments"
  | "posts";

export const toPortfolioEdit = (
  idOrSlug: string,
  tab?: PortfolioEditTabs,
  section?: string,
) =>
  `/portfolios/${idOrSlug}/edit${tab ? `?tab=${tab}` : ""}${section ? `#${section}` : ""}`;

export const toProjectPreview = (idOrSlug: string) =>
  `/projects/${idOrSlug}/preview`;
export const toPortfolioPreview = (idOrSlug: string) =>
  `/portfolios/${idOrSlug}/preview`;
export const toFundingPoolPreview = (idOrSlug: string) =>
  `/funding-pools/${idOrSlug}/preview`;

export const toProjectPost = (projectIdOrSlug: string, postIdOrSlug: string) =>
  `/projects/${projectIdOrSlug}/posts/${postIdOrSlug}`;
export const toPortfolioPost = (
  portfolioIdOrSlug: string,
  postIdOrSlug: string,
) => `/portfolios/${portfolioIdOrSlug}/posts/${postIdOrSlug}`;
export const toFundingPoolPost = (
  challengeIdOrSlug: string,
  postIdOrSlug: string,
) => `/funding-pools/${challengeIdOrSlug}/posts/${postIdOrSlug}`;
export const toPost = (
  entityType: PostEntityType,
  entityIdOrSlug: string,
  postIdOrSlug: string,
) => {
  switch (entityType) {
    case "projects":
      return toProjectPost(entityIdOrSlug, postIdOrSlug);
    case "portfolios":
      return toPortfolioPost(entityIdOrSlug, postIdOrSlug);
    case "challenges":
      return toFundingPoolPost(entityIdOrSlug, postIdOrSlug);
    default:
      throw new Error(`Invalid entity type: ${entityType}`);
  }
};
export const toResource = (
  entityType: PostEntityType,
  entityIdOrSlug: string,
  params?: FundingParams & {
    tab?: ProjectTabs | PortfolioTabs | FundingPoolTabs;
  },
  section?: string,
) => {
  switch (entityType) {
    case "projects":
      return toProject(
        entityIdOrSlug,
        params as (FundingParams & { tab?: ProjectTabs }) | undefined,
        section,
      );
    case "portfolios":
      return toPortfolio(entityIdOrSlug, params, section);
    case "challenges":
      return toFundingPool(entityIdOrSlug, params, section);
    default:
      throw new Error(`Invalid entity type: ${entityType}`);
  }
};

type EditResourceMap = {
  projects: ProjectEditTabs;
  portfolios: PortfolioEditTabs;
  challenges: FundingPoolEditTabs;
};

type EditResourceEntityType = keyof EditResourceMap;

export const toEditResource = <T extends EditResourceEntityType>(
  entityType: T,
  entityIdOrSlug: string,
  tab?: EditResourceMap[T],
  section?: string,
) => {
  switch (entityType) {
    case "projects":
      return toProjectEdit(entityIdOrSlug, tab as ProjectEditTabs, section);
    case "portfolios":
      return toPortfolioEdit(entityIdOrSlug, tab as PortfolioEditTabs, section);
    case "challenges":
      return toFundingPoolEdit(
        entityIdOrSlug,
        tab as FundingPoolEditTabs,
        section,
      );
    default:
      throw new Error(`Invalid entity type: ${entityType}`);
  }
};
